import { typedFrontendVendorApi } from "@vendor-app/utils/typedFrontendVendorApi";
import { StateCreator, create } from "zustand";
import { immer } from "zustand/middleware/immer";

import { useMemo } from "react";
import { MigrationFileMetadataOutput } from "@vendor-app/app/AdminRoute/AdminMigrationsRoute/Manual/libs/types";

export interface MigrationTable {
  loading: boolean;
  error: string | null;
  files: MigrationFileMetadataOutput[];

  actions: {
    fetchFiles(): Promise<void>;
    commitFileById(id: string): Promise<void>;
    downloadFeedback(id: string, type: "pre-commit" | "commit"): Promise<void>;
    downloadUploaded(id: string, name: string): Promise<void>;
    clearError(): void;
  };
}

const migrationTableSlice: StateCreator<MigrationTable, [["zustand/immer", never]]> = (set, get) => ({
  loading: false,
  error: null,
  files: [],

  actions: {
    async fetchFiles() {
      set(draft => {
        draft.loading = true;
        draft.error = null;
      });

      const response = await typedFrontendVendorApi.getReq("/migrations");

      if (response.type === "error") {
        set(draft => {
          draft.loading = false;
          draft.error = response.message ?? "Failed to fetch migration files.";
        });
        return;
      }

      set(draft => {
        draft.loading = false;
        draft.files = response.body;
      });
    },

    async commitFileById(id) {
      const response = await typedFrontendVendorApi.postReq(`/migrations/${id}/commit`);

      if (response.type === "error") {
        set(draft => {
          draft.error = response.message ?? "Commit failed";
        });
      }

      await get().actions.fetchFiles();
    },

    async downloadFeedback(id, type) {
      const endpoint =
        type === "pre-commit" ? `/migrations/${id}/pre-commit-feedback` : `/migrations/${id}/commit-feedback`;

      const response = await typedFrontendVendorApi.getReq(endpoint);

      if (response.type === "error") {
        set(draft => {
          draft.error = response.message ?? "Download failed";
        });
      }

      if (response.type === "success") {
        const blob = new Blob([response.body], { type: "text/csv;charset=utf-8" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `${type}-feedback.csv`;
        link.click();
      }
    },

    async downloadUploaded(id, name) {
      const endpoint = `/migrations/${id}/uploaded-file`;

      const response = await typedFrontendVendorApi.getReq(endpoint);

      if (response.type === "error") {
        set(draft => {
          draft.error = response.message ?? "Download failed";
        });
      }

      if (response.type === "success") {
        const blob = new Blob([response.body], { type: "text/csv;charset=utf-8" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `${name}`;
        link.click();
      }
    },

    clearError: () => set({ error: null }),
  },
});

const useMigrationTable = create<MigrationTable>()(immer(migrationTableSlice));

const initialStoreState = useMigrationTable.getState();

export const MigrationTableAccess = {
  useActions() {
    return useMigrationTable(state => state.actions);
  },

  useFiles() {
    return useMigrationTable(state => state.files);
  },

  useLoading() {
    return useMigrationTable(state => state.loading);
  },

  useError() {
    return useMigrationTable(state => state.error);
  },

  useHasFiles() {
    const files = MigrationTableAccess.useFiles();
    return useMemo(() => files.length > 0, [files]);
  },

  testing: {
    store: useMigrationTable,
    actions() {
      return useMigrationTable.getState().actions;
    },
    reset() {
      return useMigrationTable.setState(initialStoreState);
    },
  },
};
